import React from "react";
import styled from "styled-components";
import heroImage from "../assets/hero-section.jpg";

const WelcomeSection = styled.section`
  padding: 4rem 0;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at center, rgba(0, 191, 255, 0.05) 0%, transparent 70%);
    pointer-events: none;
  }

  @media (max-width: 768px) {
    padding: 2rem 0;
  }

  @media (max-width: 480px) {
    padding: 1.5rem 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;

  @media (max-width: 768px) {
    padding: 0 16px;
  }

  @media (max-width: 480px) {
    padding: 0 12px;
  }
`;

const WelcomeContent = styled.div`
  display: grid;
  grid-template-columns: 1.3fr 0.7fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 2;

  @media (max-width: 968px) {
    grid-template-columns: 1fr 0.8fr;
    gap: 3rem;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
    text-align: center;
  }

  @media (max-width: 480px) {
    gap: 1rem;
  }
`;

const TextContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  position: relative;

  @media (max-width: 768px) {
    order: 2;
  }
`;

const WelcomeTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 1.5rem;
  position: relative;
  
  background: linear-gradient(135deg, #00bfff, #1e90ff, #0066cc);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;

  &::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 0;
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #00bfff, #1e90ff);
    border-radius: 2px;
  }

  @media (max-width: 768px) {
    font-size: 3rem;
    text-align: center;
    
    &::after {
      left: 50%;
      transform: translateX(-50%);
    }
  }

  @media (max-width: 480px) {
    font-size: 2.4rem;
    margin-bottom: 1rem;
    
    &::after {
      width: 80px;
      height: 3px;
    }
  }
`;

const CompanyName = styled.span`
  display: block;
  font-size: 2.5rem;
  font-weight: 700;
  color: #1e293b;
  margin-top: 0.5rem;
  letter-spacing: -0.5px;

  @media (max-width: 768px) {
    font-size: 2.2rem;
  }

  @media (max-width: 480px) {
    font-size: 1.8rem;
  }
`;

const WelcomeDescription = styled.p`
  font-size: 1.2rem;
  line-height: 1.6;
  color: #64748b;
  margin-top: 1.5rem;
  max-width: 500px;
  font-weight: 400;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    max-width: none;
    margin: 1.5rem auto 0;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
    margin-top: 1rem;
  }
`;

const HighlightBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: linear-gradient(135deg, rgba(0, 191, 255, 0.1), rgba(30, 144, 255, 0.05));
  border: 1px solid rgba(0, 191, 255, 0.2);
  border-radius: 25px;
  padding: 0.6rem 1.2rem;
  margin-bottom: 1.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #0066cc;
  
  &::before {
    content: "✨";
    font-size: 1rem;
  }

  @media (max-width: 768px) {
    margin: 0 auto 1.5rem;
  }
`;

const ImageContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;

  @media (max-width: 768px) {
    order: 1;
    margin-bottom: 1rem;
  }

  @media (max-width: 480px) {
    margin-bottom: 0.5rem;
  }
`;

const HeroImage = styled.img`
  width: 100%;
  max-width: 280px;
  height: auto;
  border-radius: 20px;
  position: relative;
  z-index: 2;
  transition: all 0.3s ease;
  
  box-shadow: 0 20px 40px rgba(0, 191, 255, 0.15);
  border: 2px solid rgba(255, 255, 255, 0.8);

  &:hover {
    transform: translateY(-8px) scale(1.03);
    box-shadow: 0 25px 50px rgba(0, 191, 255, 0.2);
  }

  @media (max-width: 968px) {
    max-width: 250px;
  }

  @media (max-width: 768px) {
    max-width: 220px;
  }

  @media (max-width: 480px) {
    max-width: 180px;
    border-radius: 16px;
    box-shadow: 0 15px 30px rgba(0, 191, 255, 0.12);
  }
`;

const ImageBackground = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  right: -10px;
  bottom: -10px;
  background: linear-gradient(135deg, rgba(0, 191, 255, 0.1), rgba(30, 144, 255, 0.05));
  border-radius: 25px;
  z-index: 1;
  transition: all 0.3s ease;

  ${ImageContainer}:hover & {
    transform: rotate(2deg) scale(1.05);
    background: linear-gradient(135deg, rgba(0, 191, 255, 0.15), rgba(30, 144, 255, 0.08));
  }

  @media (max-width: 480px) {
    top: 8px;
    left: 8px;
    right: -8px;
    bottom: -8px;
    border-radius: 20px;
  }
`;

const Welcome = () => {
  return (
    <WelcomeSection>
      <Container>
        <WelcomeContent>
          <TextContent>
            <HighlightBadge>Premium Quality & Innovation</HighlightBadge>
            <WelcomeTitle>
              Welcome to
              <CompanyName>ASKK International Group of Co.</CompanyName>
            </WelcomeTitle>
            <WelcomeDescription>
              Discover excellence in innovation and quality. We are committed to delivering
              outstanding solutions that drive success and create lasting partnerships with our valued clients worldwide.
            </WelcomeDescription>
          </TextContent>

          <ImageContainer>
            <ImageBackground />
            <HeroImage
              src={heroImage}
              alt="ASKK International Group of Co. - Welcome"
              loading="lazy"
            />
          </ImageContainer>
        </WelcomeContent>
      </Container>
    </WelcomeSection>
  );
};

export default Welcome;